import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { useSessionContext } from 'session'
import { type TableEntity, type UsFilters, UsTable, type State, UsPeopleFilters, useUsPrefixLuts } from 'components'
import { type LegRole, type UsPeopleFilterResults, usPeopleListFilter } from 'services'
import { suffixOptions } from 'utils'

export interface PeopleFilters extends UsFilters {
  id: string | null
  state: State | null
  role: LegRole | null
  firstName: string | null
  lastName: string | null
  email: string | null
}

export const PeoplePageUS: React.FC = () => {
  const { changeModuleName, changeUniqueId, sessionData } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const [roles, setPeople] = useState<TableEntity[]>([])
  const initialState: PeopleFilters = {
    id: '',
    branch: null,
    state: null,
    role: null,
    firstName: '',
    lastName: '',
    email: '',
    page: 1,
    order_field: 'last_name',
    order_direction: 'ASC'
  }

  const [filters, setFilters] = useState<PeopleFilters>(initialState)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<PeopleFilters>(initialState)

  const additionalColumns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'Prefix',
        accessor: 'prefixId',
        order_field: 'prefix',
        Cell: ({ value }: { value: number }) => usPrefixLutValues.prefixes.find(prefix => prefix.id === value)?.code ?? ''
      },
      { Header: 'First Name', accessor: 'firstName', order_field: 'first_name' },
      { Header: 'Middle Name', accessor: 'middleName', order_field: 'middle_name' },
      { Header: 'Last Name', accessor: 'lastName', order_field: 'last_name' },
      {
        Header: 'Suffix',
        accessor: 'suffixId',
        order_field: 'suffix',
        Cell: ({ value }: { value: number | null }) => suffixOptions.find((option) => option.id === value)?.value ?? ''
      },
      { Header: 'Nickname', accessor: 'nickname', order_field: 'nickname' },
      { Header: 'Role', accessor: 'role', order_field: 'role' }
    ]
    const districtColumn = { Header: 'District or State', accessor: 'district', order_field: 'district' }
    const titleColumn = { Header: 'Title', accessor: 'title', order_field: 'title' }
    const orgCodeColumn = { Header: 'Org Code', accessor: 'orgCode', order_field: 'org_code' }

    const submittedBranch = lastQueriedFilters.branch?.id
    if (submittedBranch === 'executive') {
      return [...baseColumns, titleColumn, orgCodeColumn]
    }
    if (submittedBranch === 'legislative') {
      return [...baseColumns, districtColumn]
    }
    return [...baseColumns, districtColumn, titleColumn, orgCodeColumn]
  }, [lastQueriedFilters.branch, usPrefixLutValues.prefixes])

  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const { openNewTab } = useSessionContext()

  useEffect(() => {
    changeModuleName('People')
    changeUniqueId('')
  }, [changeModuleName])

  const updateFilters = (filters: any): void => {
    setFilters(prevState => ({ ...prevState, ...filters }))
  }

  const cleanFilter = (): void => {
    const diff = JSON.stringify(initialState) === JSON.stringify(lastQueriedFilters)
    setFilters(initialState)
    if (!diff) { setLoadResultWithFilters(prevState => !prevState) }
  }

  const fetchPeople = async (): Promise<UsPeopleFilterResults> => {
    setLoading(true)

    try {
      const params: any = {
        id: filters.id !== '' ? filters.id : undefined,
        firstName: filters.firstName !== '' ? filters.firstName : undefined,
        lastName: filters.lastName !== '' ? filters.lastName : undefined,
        email: filters.email?.trim() !== '' ? filters.email?.trim() : undefined,
        branch: filters.branch != null ? filters.branch.id : undefined,
        state: filters.state != null ? filters.state.id : undefined,
        role: filters.role != null ? filters.role.id : undefined,
        page: filters.page !== 0 ? filters.page : undefined,
        match_mode: sessionData.peopleNameMatchMode
      }
      params.order_by = filters.order_field
      params.order_direction = filters.order_direction
      params.page_size = 100
      return await usPeopleListFilter(params)
    } catch (error) {
      return { data: [], page_number: 1, page_size: 1, total_pages: 1, total_items: 0 }
    }
  }

  const updatePeopleTableWithFilter = async (): Promise<void> => {
    fetchPeople().then(response => {
      setPeople(response.data)
      setPageCount(response.total_pages)
      setTotalCount(response.total_items)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) { updateFilters({ page: response.page_number }) }
    }).catch(reason => {
      setPeople([])
      setPageCount(0)
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    const updateTablePage = async (): Promise<void> => {
      await updatePeopleTableWithFilter()
    }
    void updateTablePage()
  }, [loadResultWithFilters])

  return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box width='97%' sx={{ pb: 1 }}>
                  <UsPeopleFilters doFilter={updatePeopleTableWithFilter} filters={filters} updateFilters={updateFilters} cleanFilters={cleanFilter}/>
                  <UsTable
                    entities={roles}
                    orderField={filters.order_field}
                    orderDirection={filters.order_direction}
                    loading={loading}
                    pageCount={pageCount}
                    totalCount={totalCount}
                    triggerReload={() => { setLoadResultWithFilters(prevState => !prevState) } }
                    openEdit={(entity) => {
                      entity.id != null
                        ? openNewTab(`/us/editPerson/${entity.id}`)
                        : alert('No ID on this Person 🤔')
                    } }
                    filters={filters}
                    updateFilters={updateFilters}
                    additionalColumns={additionalColumns} openDelete={function (entity: TableEntity): void {
                      throw new Error('Function not implemented.')
                    } }/>
                </Box>
            </Box>
  )
}
